/**
 * One free read, then the price (#5631; founder yes 2026-10-27, R-93; mock A
 * of #5621, "One sheet, one plan"). Every line the second-read moment ships,
 * in one place, so the sheet, the Launchpad card, the plan line, /start,
 * /financial-aid, /pricing and the Terms cannot drift apart.
 *
 * The rules these words keep:
 *  - no "credit", no "usage", no "limit", no "unlimited", no count and no
 *    bar (product invariant 1; noCreditTalkGuard.test.js walks this file);
 *  - "free" only where nothing is charged;
 *  - the price is never typed here: it is the price hook's own label
 *    (constants/seasonPricing.js is the one clock);
 *  - a school's name comes only from the record (invariant 5): the server's
 *    get-credits names the first read's school, the surface names the
 *    second; with no name the line drops it rather than invent one;
 *  - the refund promise travels with the price, from the one constant
 *    (REFUND_POLICY_LINE, 16 CFR 239).
 *
 * The server decides who is under the rule (profile_manager_v2/free_read.py)
 * and get-credits carries `free_read: {rule, spent, first_school}` for such
 * an account only; nothing here reads the flag.
 */

// The first day a new account can be under the rule (the Terms name it).
// Pinned equal to profile_manager_v2/free_read.ONE_FREE_READ_FROM by
// tests/cloud_functions/profile_manager_v2/test_free_read.py.
export const ONE_FREE_READ_FROM_LABEL = 'October 28, 2026';

/** The sheet's headline. */
export const SHEET_HEADLINE = 'Your first full read was free';

/** The actions the sheet can be asked for, by the `feature` every gated
 * surface already passes to the sheet. A read names the second school. */
const ACTION_LINES = {
    'cost ranges': 'The ranges for your whole list come with the Season Pass.',
    'major map': 'Major Maps come with the Season Pass.',
    'major strategy': 'Major Strategies come with the Season Pass.',
    'essay outline': 'Essay outlines come with the Season Pass.',
    'essay review': 'Essay reviews come with the Season Pass.',
};

const READ_FEATURES = ['fit analysis', 'a fit analysis', 'updating this fit analysis', 'fit'];

/** "UCLA was yours. Every other college on your list, Michigan included,
 * comes with the Season Pass." The first school from the server's record;
 * the second from the surface; either absent, the line drops it. */
export function sheetLine({ firstSchool, feature, secondSchool } = {}) {
    const yours = firstSchool ? `${firstSchool} was yours. ` : '';
    const action = ACTION_LINES[String(feature || '').toLowerCase()];
    if (action) return `${yours}${action}`;
    const included = secondSchool ? `, ${secondSchool} included,` : '';
    return `${yours}Every other college on your list${included} comes with the Season Pass.`;
}

/** The button: "Get the Season Pass: $149" (Through Graduation: the switch's
 * own label; the price is the hook's). */
export const buyLabel = (priceLabel) => `Get the Season Pass: ${priceLabel}`;

/** The footnote under the button: nothing is taken back. */
export function footnote({ feature, secondSchool } = {}) {
    if (String(feature || '').toLowerCase() === 'cost ranges') {
        return 'Your aid index and Pell estimate stay on the page. Everything you made stays yours.';
    }
    const stays = secondSchool
        ? `${secondSchool} stays on your list with its odds. `
        : 'Every college stays on your list with its odds. ';
    return `${stays}Everything you made stays yours.`;
}

export const NOT_NOW = 'Not now';

/** The Launchpad card of a school with no full read, under the school door. */
export const CARD_LINE = 'The quick read. Its full read, with the plan, comes with the Season Pass.';

/** The plan line under the header, for an account under the rule at zero. */
export const PLAN_LINE = 'Your first full read was free. The Season Pass covers the rest of your list, all season.';
export const PLAN_LINE_CTA = 'See the Season Pass';

/** Universities: the add control's words (today's "Add - unlock your fit
 * analysis" named the analysis as a lock; the add is free, the sheet says
 * what the Pass adds). */
export const EXPLORER_ADD = 'Add to your list';

/** /start, a second school's line (its link opens the sheet). */
export const START_SECOND_SCHOOL = 'It is on your list. Its full read comes with the Season Pass.';

/** /financial-aid, the ranges after the first school. */
export const RANGES_REST_LINE = 'The rest of your list comes with the Season Pass.';

/** /pricing's Free card, under the rule. */
// /pricing's free line under the Season Pass card for a reader under the
// rule (#5909): the 7-day trial is not offered to them, so the line never
// promises it. components/pricing/OnePlan.jsx freeLine reads it.
export const PRICING_FREE_LINE_ONE_READ = 'Free: your first full college read, your odds at every school, your list, and Astra for questions.';
export const FREE_PLAN_DESCRIPTION_ONE_READ = 'See where you stand at every college, and your first full college read, free.';

/** The Terms' Free Tier bullets, with the date. */
export const TERMS_FREE_READ_LINE = `Accounts made from ${ONE_FREE_READ_FROM_LABEL}: your first full college read is free (fit, major chances and the plan). Every other full read, Major Map, essay outline and review, and the ranges for your whole list come with the Season Pass. Accounts made before that date keep the free full reads they were given.`;
export const TERMS_TRIAL_BEFORE_LINE = `The 7 days are offered to accounts made before ${ONE_FREE_READ_FROM_LABEL}; an account made from that date has its first full read free instead.`;

/** Astra and the connector (the app and Claude): the line before the
 * confirm card for a free account under the rule at zero. */
export const ASTRA_LINE = 'Your first full read was free. This one comes with the Season Pass, all season.';
